/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import * as EmailInput from 'global_elements/Inputs/TextInput/lib/email';
import Button from 'global_elements/Button';
import MHODateTime from 'domain/dateTime/MHODateTime';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import Paragraph from 'global_elements/Text/Paragraph';
import { ButtonVariant } from 'global_elements/Button/variants';
import { DisplayVariant, JustifyVariant, AlignVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { ProviderAssessmentCardProps } from 'types/cardProps';
import { ProviderRoutes } from 'constants/routes';
import { ReactComponent as X } from 'icons/mho-x-icon.svg';

import './styles.scss';
import PopupWindow from 'global_elements/Layouts/PopupWindow';
import { useEffect, useRef, useState } from 'react';
import TertiaryHeader from 'global_elements/Text/TertiaryHeader';
import InlineText from 'global_elements/Text/InlineText';
import LabeledTextInput from 'global_elements/Inputs/TextInput/LabeledTextInput';
import { TextInputVariant } from 'global_elements/Inputs/TextInput/variants';
import AssessmentProxy from 'api/assessments/assessmentProxy';
import { UserContext } from 'context/user';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { InstrumentTypes } from 'constants/assessment_types';
import LabeledSingleSelect from '../../../Inputs/Dropdown/SingleSelect/LabeledSingleSelect';
import { SingleStandardDropdownStyles } from '../../../Inputs/Dropdown/SingleSelect/styles';
import { SelectOption } from '../../../../types/inputProps';

const ProviderAssessmentCard = (props: ProviderAssessmentCardProps): React.JSX.Element => {
  const history = useHistory();
  const { user } = React.useContext(UserContext);
  const searchParams = new URLSearchParams();
  const [popupWindowShown, setPopupWindowShown] = useState<boolean>(false);
  const [assessmentLinkEmail, setAssessmentLinkEmail] = useState<string>('');
  const [assessmentLinkLanguage, setAssessmentLinkLanguage] = React.useState<SelectOption>();
  const [deleteAssessmentDocumentId, setDeleteAssessmentDocumentId] = useState<number>();
  const [showDeleteAssessmentWindow, setShowDeleteAssessmentWindow] = useState<boolean>(false);
  const [deleteAssessmentError, setDeleteAssessmentError] = useState<string>('');
  const [languageOptions] = React.useState<SelectOption[]>([
    { label: 'English', value: '1' },
    { label: 'Spanish', value: '2' },
  ]);

  if (props?.completed) {
    searchParams.append('date', props.completed);
    searchParams.append('awid', props.appliedWhenID.toString());
  }
  const resultsClickUrl = `${ProviderRoutes.ASSESSMENT_RESULTS}/${props.careID}/${props.instrumentTypeID}/${props.patientID}`;
  const startClickUrl = `${ProviderRoutes.ASSESSMENT}/${props.id}/${props.instrumentTypeID}/${props.appliedWhenID}/${props.patientID}`;

  const hasBeenSentBefore = (): boolean => props.assessmentLinkEmail !== null && props.assessmentLinkSentDate !== null;
  const invalidEmail = (): boolean => !EmailInput.validEmail(assessmentLinkEmail);
  const frozenClass = props.submissionFrozen ? 'frozen' : '';
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const ref = useRef<SVGSVGElement>(null);

  const handleClickOutside = (event: MouseEvent): any => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsMenuOpen(!isMenuOpen);
    }
  };

  const getFormattedPixDate = (completedDateString: string): string => {
    const completedDate = new MHODateTime(completedDateString);

    return `${completedDate.getFormattedMonth()}/${completedDate.getRawFullYear()}`;
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      if (isMenuOpen) {
        document.removeEventListener('click', handleClickOutside);
      }
    };
  });

  const openEmailModal = (): void => {
    setPopupWindowShown(true);
  };

  const closeEmailModal = (): void => {
    setPopupWindowShown(false);
  };

  const getTimeString = (): string => {
    if (props.assessmentLinkSentDate) {
      return new MHODateTime(props.assessmentLinkSentDate).getJSDate().toLocaleString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
    return '';
  };

  const sendAssessmentLinkEmail = (): void => {
    AssessmentProxy.sendAssessmentLink({
      assessmentLink: {
        accountID: user ? parseInt(user.accountId, 10) : -1,
        patientID: props.patientID,
        assessmentLinkEmail,
        languageID: parseInt((assessmentLinkLanguage ?? languageOptions[0]).value, 10),
      },
      assessmentLinkAssessment: [
        {
          instrumentTypeID: props.instrumentTypeID,
          assessmentNumber: parseInt(props.assessmentNumber, 10),
          appliedWhenID: props.appliedWhenID,
        },
      ],
    }).then(() => {
      setAssessmentLinkEmail('');
      setAssessmentLinkLanguage(languageOptions[0]);
      closeEmailModal();
      window.location.reload();
    });
  };

  const deleteAssessmentClicked = (documentId: number): void => {
    setDeleteAssessmentDocumentId(documentId);
    setShowDeleteAssessmentWindow(true);
  };

  const deleteAssessment = (): void => {
    if (!deleteAssessmentDocumentId) {
      setDeleteAssessmentError('Error: No document ID specified for deletion.');
      return;
    }
    AssessmentProxy.deleteDocument(deleteAssessmentDocumentId).then((r) => {
      if (!r.success) {
        setDeleteAssessmentError(r.message ?? '');
      } else {
        setDeleteAssessmentError('');
        setShowDeleteAssessmentWindow(false);
        window.location.reload();
      }
    });
  };

  return (
    <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.END} extraClasses={`provider-assessment-card-wrapper ${frozenClass}`}>
      {/* Do not show the "Applied When" label when it's a core measures assessment */}
      {!props.isCoreMeasures && (
        <FlexContainer
          display={DisplayVariant.FLEX_ROW}
          justify={JustifyVariant.CENTER}
          align={AlignVariant.CENTER}
          extraClasses={`provider-assessment-card-type --${props.appliedWhen.toString().toLowerCase()}`}
        >
          <Paragraph text={props.appliedWhen} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.EXTRA_SMALL} bold />
          {((props.showEdit && props.awidEditable && props.assessmentStatus === 'Completed') || props.showDelete) && (
            <>
              <FontAwesomeIcon
                ref={ref}
                icon={faEllipsis}
                style={{ marginLeft: '5px', color: '#fff', height: '.8rem', cursor: 'pointer', border: '1px solid #ccc', padding: '0px 2px' }}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              />
              {isMenuOpen && (
                <div className="provider-assessment-card-menu">
                  {props.showEdit && props.awidEditable && props.assessmentStatus === 'Completed' && (
                    <div
                      className="provider-assessment-card-menu-item"
                      onClick={(e) => {
                        if (props.onEditAppliedWhen) {
                          props.onEditAppliedWhen(e);
                        }
                        setIsMenuOpen(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faPencil} />
                      &nbsp;
                      <span>Edit...</span>
                    </div>
                  )}
                  {props.showDelete && (
                    <div
                      className="provider-assessment-card-menu-item"
                      onClick={() => {
                        deleteAssessmentClicked(props.documentID);
                        setIsMenuOpen(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      &nbsp;
                      <span>Delete...</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </FlexContainer>
      )}

      <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.START} extraClasses="provider-assessment-card-content">
        <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="provider-assessment-card-content-header">
          {props.allowSelection && (
            <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START}>
              <input key={`${props.assessmentNumber}_${props.appliedWhenID}_${props.sequenceNumber}`} type="checkbox" onChange={props.onSelected} />
            </FlexContainer>
          )}
          <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="provider-assessment-card-content-header-text">
            <Paragraph
              text={`${props.instrumentType} ${props.instrumentTypeID === InstrumentTypes.PIX && props.completed ? '' : `(${props.assessmentNumber})`}`}
              fontColor={FontColors.SECONDARY}
              fontSize={FontSizes.LARGE}
              extraClasses="--limit-width"
              bold
            />
            <Paragraph text={props.title} fontColor={FontColors.DARK} fontSize={FontSizes.REGULAR} />
            {props.instrumentTypeID === InstrumentTypes.PIX && (
              <Paragraph text="Complete within 24 hours of discharge" fontColor={FontColors.DARK} fontSize={FontSizes.SMALL} extraClasses="pix-warning-message" bold />
            )}
          </FlexContainer>
        </FlexContainer>
        <FlexContainer
          display={DisplayVariant.FLEX_ROW}
          justify={props.completed ? JustifyVariant.SPACE_BETWEEN : JustifyVariant.END}
          align={AlignVariant.START}
          extraClasses="provider-assessment-card-bottom"
        >
          <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.START} extraClasses="provider-assessment-card-cta">
            <div className="completed-when">
              {props.completed && (
                <Paragraph
                  text={`Completed: ${props.instrumentTypeID === InstrumentTypes.PIX ? getFormattedPixDate(props.completed) : new MHODateTime(props.completed).getFormattedCalendarDate()}`}
                  fontColor={FontColors.DARK}
                  fontSize={FontSizes.SMALL}
                  extraClasses={`${frozenClass}`}
                  bold
                />
              )}
            </div>
            {props.availableForPatient === 1 && !props.isCoreMeasures && props.instrumentTypeID !== InstrumentTypes.PIX && (
              <Button variant={ButtonVariant.INVISIBLE} onClick={openEmailModal} testid="send-resend-button">
                <Paragraph text={hasBeenSentBefore() ? 'Resend' : 'Send'} fontColor={FontColors.SECONDARY} fontSize={FontSizes.SMALL} underlined />
              </Button>
            )}
            {props.ctas.map((cta) => (
              <Button
                variant={ButtonVariant.INVISIBLE}
                onClick={() => {
                  if (cta === 'Results') {
                    history.push({
                      pathname: resultsClickUrl,
                      search: searchParams.toString(),
                    });
                  } else if (cta === 'Tablet mode') {
                    if (props.showSelectAssessmentsWindow) {
                      props.showSelectAssessmentsWindow(true);
                    } else {
                      console.log('ERROR: Tablet mode function not found.');
                    }
                  } else {
                    history.push(startClickUrl, {
                      sequenceNumber: props.sequenceNumber ? props.sequenceNumber : 1,
                      loadAnswersFlag: props.loadAnswersFlag,
                      dischargeDisp: props.dischargeDisp,
                      redo: cta === 'Redo' ? 1 : 0,
                    });
                  }
                }}
              >
                {/* ****history accessment card fontsize */}
                <Paragraph text={cta} fontColor={FontColors.SECONDARY} fontSize={FontSizes.REGULAR} underlined />
              </Button>
            ))}
          </FlexContainer>
        </FlexContainer>
        {popupWindowShown && (
          <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="popup-window-wrapper" testid="popup-window-wrapper">
            <PopupWindow>
              <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="popup-window-content">
                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.CENTER} extraClasses="popup-window-header">
                  <TertiaryHeader text={`Send Assessment Link for ${props.instrumentType} (${props.assessmentNumber})`} fontColor={FontColors.PRIMARY} />
                  <Button
                    variant={ButtonVariant.INVISIBLE}
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      closeEmailModal();
                    }}
                  >
                    <X />
                  </Button>
                </FlexContainer>
                {hasBeenSentBefore() && (
                  <Paragraph
                    text={`Email previously sent to ${props.assessmentLinkEmail} on ${getTimeString()} (CT).`}
                    fontColor={FontColors.DARK}
                    fontSize={FontSizes.REGULAR}
                    extraClasses="previously-sent-text"
                    testId="previously-sent-text"
                  />
                )}
                <form className="send-assessment-link" onSubmit={sendAssessmentLinkEmail}>
                  <LabeledTextInput
                    label="Email"
                    placeholder="Enter the email to send an assessment link to..."
                    name="email"
                    value={assessmentLinkEmail}
                    onChange={(e) => setAssessmentLinkEmail(e.target.value)}
                    type="email"
                    variant={TextInputVariant.PRIMARY}
                    autoFocus
                    testid="email-input"
                  />
                  <LabeledSingleSelect
                    styles={SingleStandardDropdownStyles}
                    label="Language"
                    options={languageOptions}
                    defaultValue={languageOptions[0]}
                    value={assessmentLinkLanguage}
                    onSelection={(l) => setAssessmentLinkLanguage(l)}
                  />
                  <Button variant={ButtonVariant.PRIMARY} disabled={invalidEmail()} submit onClick={sendAssessmentLinkEmail} testid="submit-send-resend-button">
                    <InlineText text={hasBeenSentBefore() ? 'Resend' : 'Send'} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
                  </Button>
                </form>
              </FlexContainer>
            </PopupWindow>
          </FlexContainer>
        )}
        {showDeleteAssessmentWindow && (
          <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="popup-window-wrapper">
            <PopupWindow>
              <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="popup-window-content">
                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.CENTER} extraClasses="popup-window-header">
                  <TertiaryHeader text="Delete Assessment?" fontColor={FontColors.PRIMARY} />
                  <Button
                    variant={ButtonVariant.INVISIBLE}
                    onClick={(e) => {
                      e?.preventDefault();
                      e?.stopPropagation();
                      setShowDeleteAssessmentWindow(false);
                    }}
                  >
                    <X />
                  </Button>
                </FlexContainer>
                <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="delete-assessment-window-messaging">
                  <Paragraph text="Are you sure you want to delete this assessment? This cannot be undone." fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.REGULAR} />
                </FlexContainer>
                {deleteAssessmentError !== '' && <Paragraph text={deleteAssessmentError} fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.REGULAR} extraClasses="error-message" bold />}
                <Button variant={ButtonVariant.HIGH_PRIORITY} onClick={deleteAssessment}>
                  <InlineText text="Delete" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
                </Button>
              </FlexContainer>
            </PopupWindow>
          </FlexContainer>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

export default React.memo(ProviderAssessmentCard);
